<template>
  <section class="max-w-[1440px] mx-auto px-12 py-12 space-y-12">

    <!-- HEADER -->
    <header>
      <h2 class="text-4xl font-black tracking-tight">
        Discover Influencers
      </h2>
      <p class="text-slate-400 mt-2 max-w-2xl">
        Search and evaluate creators using verified audience intelligence.
        No scraped metrics. No vanity numbers.
      </p>
    </header>

    <!-- SEARCH & FILTERS -->
    <section class="bg-white p-8 rounded-[2rem] premium-shadow border border-slate-50
             grid grid-cols-1 md:grid-cols-6 gap-6">
      <!-- Search -->
      <div class="md:col-span-2">
        <label class="filter-label">Search</label>
        <input v-model="filters.search" placeholder="Creator name…" class="filter-input" />
      </div>

      <!-- Platform -->
      <div>
        <label class="filter-label">Platform</label>
        <select v-model="filters.platform" class="filter-input">
          <option value="">All</option>
          <option>Instagram</option>
          <option>YouTube</option>
          <option>LinkedIn</option>
          <option>X</option>
        </select>
      </div>

      <!-- Category -->
      <div>
        <label class="filter-label">Category</label>
        <select v-model="filters.category" class="filter-input">
          <option value="">All</option>
          <option>Technology</option>
          <option>Education</option>
          <option>Finance</option>
          <option>Fitness</option>
          <option>Marketing</option>
        </select>
      </div>

      <!-- Audience -->
      <div>
        <label class="filter-label">Audience Size</label>
        <select v-model="filters.audience" class="filter-input">
          <option value="">Any</option>
          <option value="small">10K – 50K</option>
          <option value="medium">50K – 250K</option>
          <option value="large">250K+</option>
        </select>
      </div>

      <!-- Reset -->
      <div class="flex items-end">
        <button @click="resetFilters" class="text-xs font-bold uppercase tracking-wider
                 text-slate-400 hover:text-slate-900 transition">
          Reset
        </button>
      </div>
    </section>

    <!-- RESULTS -->
    <!-- RESULTS -->
    <section v-if="influencers.length"
      class="bg-white rounded-[2rem] premium-shadow border border-slate-50 overflow-hidden">

      <div class="overflow-x-auto">
        <table class="w-full text-sm">

          <!-- HEADER -->
          <thead class="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th @click="sortBy('name')" class="px-6 py-4 text-left cursor-pointer hover:text-slate-900">
                Creator
                <span v-if="sortKey==='fans'">
                  {{ sortOrder === 'asc' ? '▲' : '▼' }}
                </span>
              </th>

              <th @click="sortBy('platform')" class="px-6 py-4 text-left cursor-pointer hover:text-slate-900">
                Platform
                <span v-if="sortKey==='platform'">
                  {{ sortOrder === 'asc' ? '▲' : '▼' }}
                </span>
              </th>

              <th @click="sortBy('category')" class="px-6 py-4 text-left cursor-pointer hover:text-slate-900">
                Category
                <span v-if="sortKey==='category'">
                  {{ sortOrder === 'asc' ? '▲' : '▼' }}
                </span>
              </th>

              <th @click="sortBy('fans')" class="px-6 py-4 text-left cursor-pointer hover:text-slate-900">
                Verified Fans
                <span v-if="sortKey==='fans'">
                  {{ sortOrder === 'asc' ? '▲' : '▼' }}
                </span>
              </th>

              <th @click="sortBy('aud_quality')" class="px-6 py-4 text-left cursor-pointer hover:text-slate-900">
                Avg Fan Quality
                <span v-if="sortKey==='aud_quality'">
                  {{ sortOrder === 'asc' ? '▲' : '▼' }}
                </span>
              </th>
              
              <th class="px-6 py-4 text-left">Actions</th>
            </tr>
          </thead>

          <!-- BODY -->
          <tbody class="divide-y">

            <tr v-for="i in paginatedInfluencers" :key="i.id" class="hover:bg-slate-50 transition">

              <!-- CREATOR -->
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  <div
                    class="size-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">
                    {{ i.name.charAt(0) }}
                  </div>

                  <div>
                    <p class="font-semibold text-slate-900">
                      {{ i.name }}
                    </p>
                  </div>
                </div>
              </td>

              <!-- PLATFORM -->
              <td class="px-6 py-4 font-medium">
                {{ i.platform }}
              </td>

              <!-- CATEGORY -->
              <td class="px-6 py-4">
                {{ i.category }}
              </td>

              <!-- FANS -->
              <td class="px-6 py-4 font-semibold">
                {{ i.fans }}
              </td>

              <!-- INTERESTS -->
              <td class="px-6 py-4 text-slate-600">
                {{ i.aud_quality }}<br>
                {{ i.aud_quality_typ }}
              </td>

              <!-- ACTIONS -->
              <td class="px-6 py-4 text-right">
                <div class="flex gap-3">

                  <button @click="gotoprofile(i)" class="px-4 py-2 text-xs font-bold uppercase tracking-wider
                       bg-primary text-white rounded-lg hover:opacity-90">
                    Profile
                  </button>

                  <button @click="addToShortlist(i)" class="px-4 py-2 text-xs font-bold uppercase tracking-wider
                       border border-slate-200 rounded-lg hover:bg-slate-50">
                    {{ i.shortlisted ? "Interested" : "Add to Interest" }}
                  </button>

                </div>
              </td>

            </tr>

          </tbody>
        </table>
      </div>

    </section>

    <!-- EMPTY -->
    <div v-else class="text-center py-24 text-slate-400">
      <h3 class="text-xl font-bold text-slate-900">
        No influencers found
      </h3>
      <p class="mt-2">
        Try adjusting filters or search criteria.
      </p>
    </div>

    <!-- PAGINATION -->
    <section v-if="totalPages > 1" class="flex justify-center items-center gap-6 pt-8">
      <button :disabled="page === 1" @click="page--" class="pagination-btn">
        Prev
      </button>

      <span class="text-xs font-bold text-slate-400">
        Page {{ page }} of {{ totalPages }}
      </span>

      <button :disabled="page === totalPages" @click="page++" class="pagination-btn">
        Next
      </button>
    </section>

  </section>
</template>
<script>
  import { discoverCreatorsApi } from "../apis/agencyapis1";
  import { addToShortlistApi, removeShortlistApi } from "../apis/shortapis";
  import { useToast } from "vue-toastification";

  export default {
    name: "DiscoverInfluencers",

    data() {
      return {
        sortKey: "",
        sortOrder: "asc",
        user_id: "",
        toast: null,
        page: 1,
        perPage: 10,

        filters: {
          search: "",
          platform: "",
          category: "",
          audience: ""
        },

        influencers: [],
        total_records: 0   // ✅ Needed for real pagination
      };
    },

    async mounted() {
      this.toast = useToast();
      try {

        const ldataRaw = sessionStorage.getItem("ldata");

        if (ldataRaw && ldataRaw !== "undefined") {

          const ldata = JSON.parse(ldataRaw);

          if (ldata && ldata.user_id) {
            this.user_id = ldata.user_id;
          }
        }

      } catch (e) {

        console.log("Session parse error", e);
      }

      if (this.user_id) {
        await this.loadCreators();
      }
    },

    computed: {
      filteredInfluencers() {

        const q = this.filters.search.toLowerCase();

        let data = this.influencers.filter(i => {
          return (
            (!q || i.name.toLowerCase().includes(q)) &&
            (!this.filters.platform || i.platform === this.filters.platform) &&
            (!this.filters.category || i.category === this.filters.category)
          );
        });

        if (this.sortKey) {

          data.sort((a, b) => {

            let v1 = a[this.sortKey];
            let v2 = b[this.sortKey];

            if (!isNaN(v1) && !isNaN(v2)) {
              v1 = Number(v1);
              v2 = Number(v2);
            }

            if (v1 < v2) return this.sortOrder === "asc" ? -1 : 1;
            if (v1 > v2) return this.sortOrder === "asc" ? 1 : -1;

            return 0;

          });

        }

        return data;

      },

      totalPages() {
        return Math.ceil(this.total_records / this.perPage);   // ✅ Backend driven
      },

      paginatedInfluencers() {
        return this.filteredInfluencers;   // ✅ Already paginated by API
      }
    },

    watch: {
      filters: {
        deep: true,
        handler() {
          this.page = 1;
          this.loadCreators();
        }
      },

      page() {
        this.loadCreators();   // ✅ Real page switching
      }
    },

    methods: {
      sortBy(key) {

        if (this.sortKey === key) {
          this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
        } else {
          this.sortKey = key;
          this.sortOrder = "asc";
        }

      },
      async addToShortlist(inf) {
        if (inf.shortlisted) {
          const res = await removeShortlistApi(
            this.user_id,
            inf.id
          );

          if (res && res.status) {
            this.toast.success("Removed");
            this.loadCreators();

          } else {

            this.toast.error("Remove failed");
          }
        }
        else {
          const res = await addToShortlistApi({
            agency_id: this.user_id,
            campaign_id: "",   // optional
            influencer_id: inf.id,
            inf_name: inf.name,
            inf_platform: inf.platform,
            inf_category: inf.category,
            inf_fans: inf.fans
          });

          if (res && res.status) {
            this.toast.success("Interested");
            this.loadCreators();

          } else {

            this.toast.error("Interest failed");
          }
        }
      },
      async loadCreators() {

        const res = await discoverCreatorsApi({

          agency_id: this.user_id,
          search: this.filters.search,
          platform: this.filters.platform,
          category: this.filters.category,
          audience: this.filters.audience,

          page_no: (this.page - 1) * this.perPage   // ✅ Correct offset logic
        });

        if (res && res.status) {

          if (res.records && res.records.length > 0) {

            this.influencers = res.records;
            this.total_records = res.total || 0;

          } else {

            console.log("Empty records");

            this.total_records = 1;

            // ✅ Placeholder
            this.influencers = [];
          }

        } else {

          console.log("Discover API failed");

          this.total_records = 1;

          // ✅ Placeholder (Failure Safe)
          this.influencers = [
            {
              id: "0",
              name: "Unable to Load Creators",
              platform: "—",
              category: "—",
              fans: "0",
              interests: ["Retry"],
              education: "Platform Error"
            }
          ];
        }
      },

      gotoprofile(user) {
        window.location.href = "/agency/influencer-profile/" + user.id;
      },

      resetFilters() {
        this.filters = {
          search: "",
          platform: "",
          category: "",
          audience: ""
        };
      }
    }
  };
</script>


<style scoped>
  .filter-label {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: #94a3b8;
  }

  .filter-input {
    width: 100%;
    margin-top: 6px;
    padding: 14px 16px;
    border-radius: 1rem;
    background: #f8fafc;
    font-weight: 600;
  }

  .pagination-btn {
    padding: 10px 18px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    background: #f1f5f9;
  }

  /* ===============================
   DISCOVER INFLUENCERS – MOBILE PATCH
   =============================== */
  @media (max-width: 640px) {

    /* Page padding (override Tailwind px-12) */
    .px-12 {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    .py-12 {
      padding-top: 24px !important;
      padding-bottom: 48px !important;
    }

    /* Header */
    header h2 {
      font-size: 26px !important;
      line-height: 1.2;
    }

    header p {
      font-size: 14px;
    }

    /* Filters card */
    .filter-input {
      padding: 12px 14px;
      font-size: 14px;
      border-radius: 12px;
    }

    .filter-label {
      font-size: 10px;
    }

    /* Reset button alignment */
    section .flex.items-end {
      align-items: flex-start;
    }

    section .flex.items-end button {
      margin-top: 6px;
    }

    /* Influencer cards */
    section>.grid>div {
      padding: 16px !important;
      border-radius: 18px !important;
    }

    /* Avatar */
    .size-12 {
      width: 40px !important;
      height: 40px !important;
      font-size: 16px !important;
    }

    /* Card header text */
    h4 {
      font-size: 15px;
    }

    p.text-xs {
      font-size: 11px;
    }

    /* Metrics spacing */
    .space-y-3 {
      gap: 8px;
    }

    .space-y-3 span {
      font-size: 12px;
    }

    .space-y-3 strong {
      font-size: 13px;
      max-width: 60%;
      text-align: right;
    }

    /* Actions */
    .mt-8 {
      margin-top: 20px !important;
    }

    button.w-full {
      padding-top: 12px !important;
      padding-bottom: 12px !important;
      font-size: 11px !important;
    }

    /* Pagination */
    .pagination-btn {
      padding: 8px 14px;
      font-size: 10px;
    }

    /* Empty state */
    .text-center.py-24 {
      padding-top: 64px !important;
      padding-bottom: 64px !important;
    }

    .text-center h3 {
      font-size: 18px;
    }

    .text-center p {
      font-size: 14px;
    }
  }
</style>